import {
  Box,
  Button,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { IoWarningOutline } from 'react-icons/io5';
import { LuChevronRight, LuDollarSign } from 'react-icons/lu';
import { MdOutlineClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { ContractOrdersSummary } from 'services/interfaces/order';
import { getContractOrders } from 'services/order';
import { R$ } from 'shared/helpers/currency';
import { getStatusLabel } from 'shared/helpers/orderStatus';
import { Order, orderStatus } from 'shared/models/order';
import { Product } from 'shared/models/product';
import { useFinancialContext } from '../../FinancialContext';
import OrderStatusBadge from '../atoms/OrderStatusBadge';
import CreateOrderModal from '../modals/CreateOrderModal';
import ContractDataSection from '../molecules/ContractDataSection';
import ContractDetailsBlock from '../molecules/ContractDetailsBlock';
import ContractDetailsSection from '../molecules/ContractDetailsSection';
import ContractMainDataSection from '../molecules/ContractMainDataSection';
import ContractDetailsPageHeader from '../molecules/ContractPageHeader';
import ContractPaymentMethodSection from '../molecules/ContractPaymentMethodSection';
import ContractsTablePagination from '../molecules/ContractsTablePagination';
import TotalContractCard from '../molecules/TotalContractCard';
import ContractServicesTable from '../organisms/ContractServicesTable';
import { LoadingStt } from './ContractsPage';

interface DateRangeFilter {
  startDate: string;
  endDate: string;
}
const ContractDetailsPage = () => {
  const {
    displayedContract,
    setTabIndex,
    openOrderModal,
    openAddServicesModal,
  } = useFinancialContext();
  const [orders, setOrders] = useState<Order[]>();
  const [loading, setLoading] = useState<LoadingStt>({
    stt: false,
    first: false,
  });
  const [createOrderModalOpen, setCreateOrderModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeFilter | null>({
    startDate: moment()
      .subtract(1, 'year')
      .startOf('month')
      .format('YYYY-MM-DD'),
    endDate: moment().add(1, 'year').endOf('month').format('YYYY-MM-DD'),
  });
  const [ordersFilter, setOrdersFilter] = useState<number | null>(1);
  const [orderBy, setOrderBy] = useState<'asc' | 'desc' | null>('asc');
  const [selectedPage, setSelectedPage] = useState(1);
  const [itensPerPage, setItensPerPage] = useState(10);
  const [totals, setTotals] = useState<ContractOrdersSummary>({
    totalAmount: 0,
    totalCancelledAmount: 0,
    totalOpenAmount: 0,
    totalPaidAmount: 0,
  });
  const lastStt = useRef<{
    selectedContract: number | null;
    selectedPage: number | null;
    itensPerPage: number | null;
    orderBy: 'asc' | 'desc' | null;
    dateRange: DateRangeFilter | null;
    ordersFilter: number | null;
  }>({
    selectedContract: null,
    selectedPage: null,
    itensPerPage: null,
    orderBy: null,
    dateRange: null,
    ordersFilter: null,
  });
  const totalItens = useRef(0);
  const services: Product[] =
    displayedContract?.products.filter((service) => {
      return !service.isLicense && !service.deleted_at;
    }) || [];

  const handleChangePage = useCallback((selectedPage: number) => {
    setSelectedPage(selectedPage);
  }, []);

  const handleChangeOrder = useCallback((selected: 'asc' | 'desc') => {
    setOrderBy(selected);
  }, []);

  const handleChangePerPage = useCallback((itensPerPage: number) => {
    setSelectedPage(1);
    setItensPerPage(itensPerPage);
  }, []);

  const handleChangeDateRange = (
    type: 'startDate' | 'endDate',
    value: string,
  ) => {
    if (dateRange) {
      setDateRange({
        ...dateRange,
        [type]: value,
      });
    } else {
      setDateRange({
        startDate: type === 'startDate' ? value : '',
        endDate: type === 'endDate' ? value : '',
      });
    }
  };

  const clearDateRange = () => {
    setDateRange(null);
  };

  const openCreateOrderModal = () => {
    setCreateOrderModalOpen(true);
  };

  const onCloseCreateOrderModal = () => {
    setCreateOrderModalOpen(false);
  };

  const reloadOrders = useCallback(async () => {
    if (!displayedContract?.id) return;

    setLoading({ stt: true, first: false });

    let start = '';
    let end = '';
    if (dateRange?.startDate && dateRange?.endDate) {
      start = moment(dateRange.startDate).format('YYYY-MM-DD');
      end = moment(dateRange.endDate).format('YYYY-MM-DD');
    }

    try {
      const response = await getContractOrders(displayedContract.id, {
        limit: itensPerPage,
        offset: itensPerPage * (selectedPage - 1),
        orderBy,
        startDate: start,
        endDate: end,
        statusId: ordersFilter,
      });

      const orders = response.data.results.map((orderResponse) => {
        const order = new Order(orderResponse);
        order.statusLabel = getStatusLabel(order.statusId as orderStatus);
        return order;
      });

      totalItens.current = response.data.total;
      setOrders(orders);
      setTotals(response.data.summary);

      lastStt.current.selectedContract = displayedContract.id;
      lastStt.current.selectedPage = selectedPage;
      lastStt.current.itensPerPage = itensPerPage;
      lastStt.current.orderBy = orderBy;
      lastStt.current.dateRange = dateRange;
      lastStt.current.ordersFilter = ordersFilter;
    } catch (error) {
      console.error('Error reloading orders:', error);
      toast.error(
        'Erro ao recarregar as cobranças, por favor atualize a página',
      );
    } finally {
      setLoading({ stt: false, first: false });
    }
  }, [
    displayedContract?.id,
    itensPerPage,
    selectedPage,
    orderBy,
    dateRange,
    ordersFilter,
  ]);

  useEffect(() => {
    const fetchContracts = async () => {
      if (
        !loading.stt &&
        displayedContract?.id &&
        (displayedContract.id !== lastStt.current.selectedContract ||
          lastStt.current.selectedPage !== selectedPage ||
          lastStt.current.itensPerPage !== itensPerPage ||
          lastStt.current.orderBy !== orderBy ||
          lastStt.current.dateRange !== dateRange ||
          lastStt.current.ordersFilter !== ordersFilter)
      ) {
        let firstLoadStt = false;
        if (displayedContract.id !== lastStt.current.selectedContract) {
          firstLoadStt = true;
        }
        setLoading({
          stt: true,
          first: firstLoadStt,
        });
        let start = '';
        let end = '';
        if (dateRange?.startDate && dateRange?.endDate) {
          start = moment(dateRange.startDate).format('YYYY-MM-DD');
          end = moment(dateRange.endDate).format('YYYY-MM-DD');
        }
        getContractOrders(displayedContract.id, {
          limit: itensPerPage,
          offset: itensPerPage * (selectedPage - 1),
          orderBy,
          startDate: start,
          endDate: end,
          statusId: ordersFilter,
        })
          .then((response) => response.data)
          .then((response) => {
            setLoading({ stt: false, first: false });

            lastStt.current.selectedContract = displayedContract.id || null;
            lastStt.current.selectedPage = selectedPage;
            lastStt.current.itensPerPage = itensPerPage;
            lastStt.current.dateRange = dateRange || null;
            lastStt.current.ordersFilter = ordersFilter;
            lastStt.current.orderBy = orderBy;

            const orders = response.results.map((orderResponse) => {
              const order = new Order(orderResponse);
              order.statusLabel = getStatusLabel(order.statusId as orderStatus);

              return order;
            });

            totalItens.current = response.total;
            setOrders(orders);
            setTotals(response.summary);
          })
          .catch((e: AxiosError) => {
            console.error(
              'Erro ao carregar cobranças do contrato',
              e?.response?.data
                ? JSON.stringify(e?.response?.data, null, 2)
                : JSON.stringify(e?.response, null, 2),
            );
            setLoading({ stt: false, first: false });
          });
      } else if (!displayedContract?.id) {
        setLoading({ stt: false, first: false });
        setTabIndex(0);
      }
    };
    fetchContracts();
  }, [
    displayedContract,
    itensPerPage,
    selectedPage,
    dateRange,
    orderBy,
    ordersFilter,
    loading.stt,
    setTabIndex,
  ]);
  if (!displayedContract) {
    return <></>;
  }
  return (
    <>
      <ContractDataSection
        title={
          <ContractDetailsPageHeader
            loading={loading.stt && loading.first}
            contract={displayedContract}
            onBack={() => setTabIndex(0)}
          ></ContractDetailsPageHeader>
        }
      >
        <ContractMainDataSection
          loading={loading.stt && loading.first}
          contract={displayedContract}
        ></ContractMainDataSection>
        <ContractPaymentMethodSection
          loading={loading.stt && loading.first}
          contract={displayedContract}
        ></ContractPaymentMethodSection>
        <ContractDetailsSection
          loading={loading.stt && loading.first}
          contract={displayedContract}
        ></ContractDetailsSection>
        <ContractDetailsBlock
          title="Serviços"
          removeSeparator={true}
          loading={loading.stt && loading.first}
          rightButtons={[
            {
              type: 'add',
              text: 'Adicionar',
              onClick: () => openAddServicesModal(displayedContract),
              permission: 'contract-update',
            },
          ]}
        >
          <ContractServicesTable
            loading={loading.stt && loading.first}
            services={services}
          ></ContractServicesTable>
        </ContractDetailsBlock>
      </ContractDataSection>

      {(!loading.stt || (loading.stt && !loading.first)) && (
        <ContractDataSection title="Pagamentos">
          <Box>
            <Text variant="regular" marginBottom={5}>
              Clique nos cards abaixo para filtrar o status desejado.
            </Text>
            <HStack
              paddingBottom="5"
              borderBottom={'1px solid'}
              borderColor="yGray.100"
            >
              <TotalContractCard
                onClickCard={() => setOrdersFilter(1)}
                selected={ordersFilter === 1}
                icon={{ element: <IoWarningOutline />, color: 'yYellow' }}
                title="Total em aberto"
              >
                {R$(totals.totalOpenAmount)}
              </TotalContractCard>
              <TotalContractCard
                onClickCard={() => setOrdersFilter(2)}
                selected={ordersFilter === 2}
                icon={{ element: <FaCheck />, color: 'yGreen' }}
                title="Total pago"
              >
                {R$(totals.totalPaidAmount)}
              </TotalContractCard>
              <TotalContractCard
                onClickCard={() => setOrdersFilter(4)}
                selected={ordersFilter === 4}
                icon={{ element: <MdOutlineClose />, color: 'yRed' }}
                title="Total cancelados"
              >
                {R$(totals.totalCancelledAmount)}
              </TotalContractCard>
              <TotalContractCard
                onClickCard={() => setOrdersFilter(null)}
                selected={ordersFilter === null}
                icon={{ element: <LuDollarSign />, color: 'yPrimary' }}
                title="Total"
              >
                {R$(totals.totalAmount)}
              </TotalContractCard>
            </HStack>
          </Box>
          <ContractDetailsBlock
            rightButtons={[
              {
                type: 'add',
                text: 'Nova cobrança',
                onClick: () => openCreateOrderModal(),
                permission: 'order-create',
              },
            ]}
            vertical={true}
            title="Histórico de cobrança"
          >
            <HStack w="100%" mb="20px" justify="space-between">
              <HStack spacing={2}>
                <Button
                  variant="stroke"
                  fontWeight={'400'}
                  onClick={() => handleChangeOrder('asc')}
                  colorScheme={orderBy == 'asc' ? 'selected' : ''}
                  isDisabled={loading.stt && loading.first}
                >
                  Mais antigos
                </Button>

                {orderBy}
                <Button
                  variant="stroke"
                  fontWeight={'400'}
                  onClick={() => handleChangeOrder('desc')}
                  colorScheme={orderBy === 'desc' ? 'selected' : ''}
                  isDisabled={loading.stt && loading.first}
                >
                  Mais recentes
                </Button>
              </HStack>

              <HStack spacing={2} minW="200px">
                <Text fontWeight="400">Período:</Text>
                <HStack spacing={2}>
                  <Input
                    type="date"
                    placeholder="Data início"
                    value={dateRange?.startDate || ''}
                    onChange={(e) =>
                      handleChangeDateRange('startDate', e.target.value)
                    }
                    isDisabled={loading?.stt && loading.first}
                    borderWidth="1px"
                    borderColor="yGray.200"
                    color="yGray.700"
                    borderRadius="6px"
                    textTransform="none"
                    fontWeight={400}
                    fontSize="16px"
                    height="44px"
                    lineHeight="24px"
                  />
                  <Input
                    type="date"
                    placeholder="Data fim"
                    value={dateRange?.endDate || ''}
                    onChange={(e) =>
                      handleChangeDateRange('endDate', e.target.value)
                    }
                    isDisabled={loading?.stt && loading.first}
                    borderWidth="1px"
                    borderColor="yGray.200"
                    color="yGray.700"
                    borderRadius="6px"
                    textTransform="none"
                    fontWeight={400}
                    fontSize="16px"
                    height="44px"
                    lineHeight="24px"
                  />
                </HStack>
                <Button
                  variant="stroke"
                  fontWeight={'400'}
                  onClick={() => clearDateRange()}
                  isDisabled={loading.stt && loading.first}
                >
                  Limpar
                </Button>
              </HStack>
            </HStack>

            <Box w="100%">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th w="20%" textAlign="left">
                      Vencimento
                    </Th>
                    <Th w="20%" textAlign="left">
                      Valor
                    </Th>
                    <Th w="20%" textAlign="left">
                      Status
                    </Th>
                    <Th w="20%" textAlign="left">
                      Pago/Cancelado
                    </Th>
                    <Th w="20%" textAlign="left">
                      Forma de pagamento
                    </Th>
                    <Th textAlign="left"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders?.map((order) => (
                    <Tr key={order.id}>
                      <Td w="20%" textAlign="left">
                        {order.due_date?.format('DD/MM/YYYY')}
                      </Td>
                      <Td textAlign="left" w="20%">
                        {order.priceString}
                      </Td>
                      <Td w="20%" textAlign="left">
                        <OrderStatusBadge
                          order={order}
                          loading={loading.stt && loading.first}
                        ></OrderStatusBadge>
                      </Td>
                      <Td w="20%" textAlign="left">
                        {order.statusId === orderStatus.PAGO
                          ? order.complete_at?.format('DD/MM/YYYY')
                          : order.statusId === orderStatus.CANCELADO
                            ? order.cancelled_at?.format('DD/MM/YYYY')
                            : '-'}
                      </Td>
                      <Td textAlign="left" w="20%">
                        {order.bankId === 1
                          ? 'Cartão de crédito'
                          : 'Boleto Bancário'}
                      </Td>
                      <Td textAlign="left">
                        <Button
                          variant="plainIcon"
                          onClick={() => openOrderModal(order.token)}
                        >
                          <LuChevronRight />
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Box mt="5" w="100%">
              <ContractsTablePagination
                total={totalItens.current}
                selectedPage={selectedPage}
                perPage={itensPerPage}
                loading={loading}
                onPerPageChange={handleChangePerPage}
                onPageChange={handleChangePage}
              ></ContractsTablePagination>
            </Box>
          </ContractDetailsBlock>
        </ContractDataSection>
      )}
      <CreateOrderModal
        isOpen={createOrderModalOpen}
        onClose={onCloseCreateOrderModal}
        contractId={displayedContract?.id}
        contract={displayedContract}
        onOrderCreated={reloadOrders}
      />
    </>
  );
};
export default ContractDetailsPage;
